import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, ExternalLink, Newspaper, CalendarDays, Users, Trophy, UserCog, Images, FileText, Home, Building2, Phone, Link2, Inbox, KeyRound, Handshake } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { api, fmtError } from "../lib/api";
import { ResourceManager } from "../components/admin/ResourceManager";
import { SettingsEditor } from "../components/admin/SettingsEditor";
import { MessagesPanel } from "../components/admin/MessagesPanel";
import { GalleryManager } from "../components/admin/GalleryManager";
import { HomepageEditor } from "../components/admin/HomepageEditor";

const NAV = [
  ["Website Management", [
    ["bearers", "Office Bearers", UserCog], ["gallery", "Gallery Management", Images], ["players", "Player Management", Users],
    ["contact", "Contact Information", Phone], ["association", "About Association", Building2], ["news", "News & Announcements", Newspaper],
    ["sponsors", "Sponsors", Handshake], ["homepage", "Homepage Content", Home],
  ]],
  ["More Content", [
    ["events", "Events", CalendarDays], ["achievements", "Achievements", Trophy], ["documents", "Documents", FileText],
    ["affiliations", "Official Affiliations", Link2], ["messages", "Messages", Inbox], ["password", "Password", KeyRound],
  ]],
];
const SETTINGS = ["contact"];

const AboutManager = () => (
  <div className="space-y-12">
    <SettingsEditor section="association" />
    <ResourceManager resource="about_sections" />
  </div>
);

const AffiliationsManager = () => (
  <div className="space-y-12">
    <ResourceManager resource="leaders" />
    <SettingsEditor section="affiliations" />
  </div>
);

const Panel = ({ tab }) => {
  if (SETTINGS.includes(tab)) return <SettingsEditor key={tab} section={tab} />;
  const map = { messages: MessagesPanel, password: PasswordPanel, gallery: GalleryManager, homepage: HomepageEditor, association: AboutManager, affiliations: AffiliationsManager };
  const C = map[tab];
  return C ? <C /> : <ResourceManager key={tab} resource={tab} />;
};

const PasswordPanel = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password updated");
      setF({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(fmtError(err));
    }
  };
  const cls = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm";
  return (
    <form onSubmit={submit} className="max-w-md bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="admin-password-form">
      <h2 className="font-display text-2xl font-bold text-[#0B1F44]">Change password</h2>
      <input type="password" required placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={cls} data-testid="password-current" />
      <input type="password" required minLength={8} placeholder="New password (min 8 chars)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={cls} data-testid="password-new" />
      <button className="btn-primary text-sm" data-testid="password-submit">Update password</button>
    </form>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useSite();
  const [tab, setTab] = useState("bearers");
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col lg:flex-row" data-testid="admin-dashboard">
      <aside className="lg:w-64 bg-[#0B1F44] text-slate-300 lg:min-h-screen lg:sticky lg:top-0 shrink-0">
        <div className="p-5 border-b border-white/10">
          <p className="font-display text-white font-bold">TDBA Admin</p>
          <p className="text-xs text-slate-400 truncate">{user.email}</p>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {NAV.map(([group, tabs]) => (
            <div key={group} className="flex lg:flex-col gap-1 lg:mb-3">
              <p className="hidden lg:block px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-widest text-blue-300/80">{group}</p>
              {tabs.map(([k, l, Icon]) => (
                <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === k ? "bg-blue-600 text-white" : "hover:bg-white/10"}`} data-testid={`admin-tab-${k}`}>
                  <Icon className="h-4 w-4" />{l}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 flex lg:flex-col gap-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" />View website</Link>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10" data-testid="admin-logout"><LogOut className="h-4 w-4" />Log out</button>
        </div>
      </aside>
      <main className="flex-1 p-5 md:p-10 max-w-5xl">
        <Panel tab={tab} />
      </main>
    </div>
  );
}
